import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Mail, Phone, Linkedin, Download, CheckCircle2, Loader2, Repeat, CalendarDays, MapPin, Share2 } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { profil } from "../../data/content";
import { CVMenu } from "./CVMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fields = [
  { name: "nom", label: "Nom", type: "text", required: true },
  { name: "email", label: "E-mail", type: "email", required: true },
  { name: "entreprise", label: "Entreprise", type: "text", required: false },
  { name: "objet", label: "Objet", type: "text", required: true },
];

export const Contact = () => {
  const [form, setForm] = useState({ nom: "", email: "", entreprise: "", objet: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [feedback, setFeedback] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setStatus("success");
      setFeedback(data.message || "Votre message a bien été envoyé.");
      setForm({ nom: "", email: "", entreprise: "", objet: "", message: "" });
    } catch (err) {
      setStatus("error");
      setFeedback("Une erreur est survenue. Réessayez ou écrivez-moi directement par e-mail.");
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(0,229,255,0.08),transparent_60%)]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeader index="05" kicker="Contact" title="Construisons quelque chose" italic="ensemble." />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — coordonnées */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-lg text-white/90 leading-relaxed mb-10 max-w-md">
                {"Une question, une opportunité d'alternance ou simplement l'envie d'échanger ? Écrivez-moi, je réponds rapidement."}
              </p>
            </Reveal>
            <div className="space-y-3">
              <Reveal delay={0.05}>
                <a href={`mailto:${profil.email}`} className="group flex items-center justify-between border border-white/10 rounded-xl px-5 py-4 hover:border-cyan/40 transition-colors duration-300" data-testid="contact-email-link">
                  <span className="flex items-center gap-3 text-white/90"><Mail size={18} className="text-cyan" /> {profil.email}</span>
                  <ArrowUpRight size={16} className="text-[var(--text-2)] group-hover:text-cyan transition-colors" />
                </a>
              </Reveal>
              <Reveal delay={0.08}>
                <a href={`tel:${profil.telephone.replace(/\s/g, "")}`} className="group flex items-center justify-between border border-white/10 rounded-xl px-5 py-4 hover:border-cyan/40 transition-colors duration-300" data-testid="contact-phone-link">
                  <span className="flex items-center gap-3 text-white/90"><Phone size={18} className="text-cyan" /> {profil.telephone}</span>
                  <ArrowUpRight size={16} className="text-[var(--text-2)] group-hover:text-cyan transition-colors" />
                </a>
              </Reveal>
              <Reveal delay={0.1}>
                <a href={profil.linkedin} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between border border-white/10 rounded-xl px-5 py-4 hover:border-cyan/40 transition-colors duration-300" data-testid="contact-linkedin-link">
                  <span className="flex items-center gap-3 text-white/90"><Linkedin size={18} className="text-cyan" /> LinkedIn</span>
                  <ArrowUpRight size={16} className="text-[var(--text-2)] group-hover:text-cyan transition-colors" />
                </a>
              </Reveal>
              <Reveal delay={0.15}>
                <CVMenu variant="block" label="Télécharger mon CV" testid="contact-cv" align="left" />
              </Reveal>
            </div>

            {/* Rappel discret — alternance idéale */}
            <Reveal delay={0.2}>
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-5" data-testid="contact-alternance-reminder">
                <p className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-3">Mon alternance idéale</p>
                <p className="text-sm text-[var(--text-2)] leading-relaxed mb-4">
                  {"Je recherche une alternance dans le conseil, la transformation digitale, l'innovation ou la gestion de projets liés à l'IA."}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/80 border border-white/10 rounded-full px-3 py-1.5" data-testid="reminder-rythme">
                    <Repeat size={12} className="text-cyan" /> {profil.rythme}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/80 border border-white/10 rounded-full px-3 py-1.5" data-testid="reminder-dispo">
                    <CalendarDays size={12} className="text-cyan" /> {profil.disponibilite}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-white/80 border border-white/10 rounded-full px-3 py-1.5" data-testid="reminder-mobilite">
                    <MapPin size={12} className="text-cyan" /> {profil.mobilite}
                  </span>
                </div>
              </div>
            </Reveal>

            {/* Partage LinkedIn */}
            <Reveal delay={0.25}>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profil.siteUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 group flex items-center justify-between rounded-xl bg-cyan/10 border border-cyan/30 px-5 py-4 hover:bg-cyan/15 transition-colors duration-300"
                data-testid="share-linkedin-btn"
              >
                <span className="flex items-center gap-3 text-white/90 text-sm font-medium">
                  <Share2 size={17} className="text-cyan" /> Partager ce portfolio sur LinkedIn
                </span>
                <ArrowUpRight size={16} className="text-cyan group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </Reveal>
          </div>

          {/* Right — formulaire */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center text-center border border-cyan/30 rounded-2xl py-20 px-8 bg-cyan/[0.04]"
                    data-testid="contact-success"
                  >
                    <CheckCircle2 size={44} className="text-cyan mb-5" />
                    <h3 className="font-display font-medium text-2xl mb-2">Message envoyé</h3>
                    <p className="text-[var(--text-2)] max-w-sm">{feedback}</p>
                    <button onClick={() => setStatus("idle")} className="mt-8 text-sm text-cyan hover:text-white transition-colors" data-testid="contact-reset">
                      Envoyer un autre message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={onSubmit}
                    className="space-y-8"
                    data-testid="contact-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-8">
                      {fields.map((f) => (
                        <div key={f.name}>
                          <label className="block text-xs uppercase tracking-[0.2em] text-[var(--text-2)] mb-3">
                            {f.label} {f.required && <span className="text-cyan">*</span>}
                          </label>
                          <input
                            type={f.type}
                            name={f.name}
                            value={form[f.name]}
                            onChange={onChange}
                            required={f.required}
                            className="w-full bg-transparent border-b border-white/20 py-2.5 text-white placeholder-white/30 focus:border-cyan focus:outline-none transition-colors duration-300"
                            data-testid={`contact-input-${f.name}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] text-[var(--text-2)] mb-3">
                        Message <span className="text-cyan">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        required
                        rows={4}
                        className="w-full bg-transparent border-b border-white/20 py-2.5 text-white placeholder-white/30 focus:border-cyan focus:outline-none transition-colors duration-300 resize-none"
                        data-testid="contact-input-message"
                      />
                    </div>

                    {status === "error" && (
                      <p className="text-sm text-red-400" data-testid="contact-error">{feedback}</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="group inline-flex items-center gap-2 bg-cyan text-[#05070A] font-medium px-7 py-3.5 rounded-full hover:bg-white transition-colors duration-300 disabled:opacity-60"
                      data-testid="contact-submit"
                    >
                      {status === "loading" ? (
                        <><Loader2 size={18} className="animate-spin" /> Envoi…</>
                      ) : (
                        <>Envoyer le message <ArrowUpRight size={18} /></>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
